import { useQuery } from "@tanstack/react-query";
import {
  getFeatureCombinations,
  getFeatureFilters,
  getFeatureMatchingSkus,
  getProductTypes,
} from "../api/featureForecastingApi";
import {
  normalizeFeatureCombinations,
  normalizeFeatureFilters,
  normalizeProductTypes,
  normalizeSkuResponse,
} from "../utils/normalizers";

export const featureForecastingKeys = {
  productTypes: (dataUploadId) => ["feature-forecasting", "product-types", dataUploadId],
  filters: (dataUploadId, productType, selectedFilters) => [
    "feature-forecasting",
    "filters",
    dataUploadId,
    productType,
    selectedFilters,
  ],
  skus: (dataUploadId, productType, filters) => [
    "feature-forecasting",
    "matching-skus",
    dataUploadId,
    productType,
    filters,
  ],
  allCombinations: (dataUploadId, productType, baseFilters) => [
    "feature-forecasting",
    "all-combinations",
    dataUploadId,
    productType,
    baseFilters,
  ],
};

export const useProductTypesQuery = (dataUploadId) =>
  useQuery({
    queryKey: featureForecastingKeys.productTypes(dataUploadId),
    queryFn: () => getProductTypes({ dataUploadId }),
    select: normalizeProductTypes,
    enabled: Boolean(dataUploadId),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: "always",
  });

export const useFeatureFiltersQuery = ({
  dataUploadId,
  productType,
  selectedFilters,
}) =>
  useQuery({
    queryKey: featureForecastingKeys.filters(dataUploadId, productType, selectedFilters),
    queryFn: () =>
      getFeatureFilters({
        dataUploadId,
        productType,
        selectedFilters,
      }),
    select: normalizeFeatureFilters,
    enabled: Boolean(dataUploadId && productType),
    staleTime: 60 * 1000,
  });

export const useMatchingSkusQuery = ({
  dataUploadId,
  productType,
  filters,
}) =>
  useQuery({
    queryKey: featureForecastingKeys.skus(dataUploadId, productType, filters),
    queryFn: () =>
      getFeatureMatchingSkus({
        dataUploadId,
        productType,
        filters,
        limit: 500,
        offset: 0,
      }),
    select: normalizeSkuResponse,
    enabled: Boolean(dataUploadId && productType),
    staleTime: 30 * 1000,
  });

// The backend rejects large `limit` values (422), so each request stays at
// its documented default page size; to support searching/paginating across
// the *whole* result set client-side, every page is fetched and merged here.
const COMBINATIONS_FETCH_PAGE_SIZE = 50;
// Safety cap so a huge result set can't fan out into hundreds of requests.
const MAX_COMBINATIONS_TO_FETCH = 2000;

export const useAllFeatureCombinationsQuery = ({
  dataUploadId,
  productType,
  baseFilters = {},
}) =>
  useQuery({
    queryKey: featureForecastingKeys.allCombinations(dataUploadId, productType, baseFilters),
    queryFn: async () => {
      const first = normalizeFeatureCombinations(
        await getFeatureCombinations({
          dataUploadId,
          productType,
          baseFilters,
          limit: COMBINATIONS_FETCH_PAGE_SIZE,
          offset: 0,
        })
      );

      const total = first.total;
      const fetchableTotal = Math.min(total, MAX_COMBINATIONS_TO_FETCH);
      const totalPages = Math.ceil(fetchableTotal / COMBINATIONS_FETCH_PAGE_SIZE);

      const remainingPages = Array.from({ length: Math.max(0, totalPages - 1) }, (_, i) => i + 1);
      const remainingResults = await Promise.all(
        remainingPages.map((pageIndex) =>
          getFeatureCombinations({
            dataUploadId,
            productType,
            baseFilters,
            limit: COMBINATIONS_FETCH_PAGE_SIZE,
            offset: pageIndex * COMBINATIONS_FETCH_PAGE_SIZE,
          }).then(normalizeFeatureCombinations)
        )
      );

      const combinations = [first, ...remainingResults].flatMap((page) => page.combinations);
      return { combinations, total };
    },
    enabled: Boolean(dataUploadId && productType),
    staleTime: 60 * 1000,
  });

